import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";
import { STEEL_FABRICATION_SECTIONS, ELECTRICAL_SUPPORTS } from "@/data/products";

function FamilyCard({ family, index }: { family: { title: string; description: string; variations: string[] }; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-white rounded-lg p-6 border border-muted-border transition-all duration-300 hover:shadow-card-hover"
    >
      <div className="h-[3px] w-10 bg-secondary-blue rounded-full mb-4" />
      <h3 className="font-display font-bold text-lg text-graphite mb-2">{family.title}</h3>
      <p className="text-sm text-steel mb-4 leading-relaxed">{family.description}</p>
      <ul className="space-y-1.5">
        {family.variations.map((v) => (
          <li key={v} className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-steel shrink-0" />
            <span className="text-sm text-steel">{v}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function Steel() {
  const { t } = useLanguage();

  return (
    <>
      <PageHeader titleKey="steelTitle" subtitleKey="steelSubtitle" />

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="HEAVY FABRICATION" titleKey="heavyFab" descriptionKey="heavyFabDesc" align="left" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEEL_FABRICATION_SECTIONS.map((f, i) => (
              <FamilyCard key={f.title} family={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="ELECTRICAL SUPPORTS" titleKey="elecInstSupports" descriptionKey="elecInstSupportsDesc" align="left" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {ELECTRICAL_SUPPORTS.map((f, i) => (
              <FamilyCard key={f.title} family={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gold rounded-full" />
              <img src="/images/products/stainless-fabrication.jpg" alt="Stainless steel" className="w-full rounded-lg aspect-[4/3] object-cover ml-[3px]" loading="lazy" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
              <p className="label-overline text-gold mb-2">STAINLESS STEEL</p>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-primary-deep mb-4">{t("stainlessSteel")}</h2>
              <p className="text-steel leading-relaxed mb-5">{t("stainlessSteelDesc")}</p>
              <ul className="space-y-1.5">
                {["Stainless brackets", "Stainless mounting plates", "Stainless covers", "Stainless frames", "Stainless stands", "Stainless supports", "Stainless custom assemblies"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-steel shrink-0" />
                    <span className="text-sm text-steel">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="CUSTOM MANUFACTURING" titleKey="customProject" descriptionKey="customProjectDesc" align="center" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="bg-white rounded-lg p-6">
              <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("inputsAccepted")}</h3>
              <ul className="space-y-2">
                {["PDF drawings", "DWG files", "DXF files", "BOQs", "Sketches", "Marked-up drawings", "Sample part references", "Material schedules"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-secondary-blue shrink-0" />
                    <span className="text-sm text-steel">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-white rounded-lg p-6">
              <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("typicalOutputs")}</h3>
              <ul className="space-y-2">
                {["Custom brackets", "Modified supports", "Special plates", "Site-specific frames", "Custom stands", "Non-standard covers", "Special fabricated assemblies", "Coated custom items"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-secondary-blue shrink-0" />
                    <span className="text-sm text-steel">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection titleKey="needSteel" descKey="needSteelDesc" bgColor="dark" />
    </>
  );
}
